import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, FileSpreadsheet, Eye, AlertTriangle, CheckCircle2, XCircle, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime, fmtDate, todayISO } from "@/lib/utils-app";
import { ImageLightbox } from "@/components/ImageLightbox";

const ASSET_TYPE_LABEL = {
  road: "Road",
  osr: "OSR",
  footpath: "Footpath",
  sipcot_po_office: "SIPCOT PO office",
  dumping_yard: "Dumping yard",
  other: "Other",
};

function StatusPill({ value }) {
  if (value === true)
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Yes</span>;
  if (value === false)
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800"><XCircle className="h-3 w-3" /> No</span>;
  return <span className="text-muted-foreground">—</span>;
}

function MatchPill({ value }) {
  if (value === false)
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800"><AlertTriangle className="h-3 w-3" /> Mismatch</span>;
  if (value === true)
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Match</span>;
  return <span className="text-muted-foreground text-xs">—</span>;
}

export default function CleaningProgressPage() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState({ rows: [], totals: { parks: 0, before_submitted: 0, after_submitted: 0, plan_mismatches: 0 } });
  const [loading, setLoading] = useState(false);
  const [drillId, setDrillId] = useState(null);
  const [drill, setDrill] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleaning-checklists/day-progress", { params: { date } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const downloadXlsx = async () => {
    try {
      const res = await api.get("/admin/cleaning-checklists/export.xlsx", {
        params: { date },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cleaning_progress_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const openDrill = async (row) => {
    setDrillId(row.park_id);
    setDrill({ loading: true, before: null, after: null, row });
    try {
      // Fetch checklists for this park on this date with full photos
      const { data: list } = await api.get("/admin/cleaning-checklists", {
        params: { park_id: row.park_id, date, include_photos: true },
      });
      const before = (list || []).find((c) => c.kind === "before") || null;
      const after = (list || []).find((c) => c.kind === "after") || null;
      setDrill({ loading: false, before, after, row });
    } catch (e) {
      setDrill({ loading: false, before: null, after: null, row, error: true });
      toast.error("Failed to load checklist");
    }
  };

  const totals = useMemo(() => data.totals || {}, [data]);
  const rows = data.rows || [];

  const kpis = useMemo(() => ([
    { label: "Parks", value: totals.parks ?? 0, hint: "total active parks" },
    { label: "Before submitted", value: `${totals.before_submitted ?? 0} / ${totals.parks ?? 0}`, accent: "emerald" },
    { label: "After submitted", value: `${totals.after_submitted ?? 0} / ${totals.parks ?? 0}`, accent: "amber" },
    { label: "Plan mismatches", value: totals.plan_mismatches ?? 0, accent: (totals.plan_mismatches > 0) ? "rose" : "muted" },
  ]), [totals]);

  return (
    <AppShell
      title="Cleaning progress"
      subtitle="Day-wise Before / After cleaning checklist status across all parks"
      action={
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cleaning-progress-date" />
          </div>
          <Button onClick={downloadXlsx} variant="outline" data-testid="cleaning-progress-export">
            <FileSpreadsheet className="mr-2 h-4 w-4" /> Download Excel
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {kpis.map((k, i) => (
          <Card key={i} className={k.accent === "rose" ? "border-rose-200" : k.accent === "emerald" ? "border-emerald-200" : k.accent === "amber" ? "border-amber-200" : ""}>
            <CardContent className="p-3">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <div className={`font-display text-2xl font-semibold ${k.accent === "rose" ? "text-rose-700" : k.accent === "emerald" ? "text-emerald-700" : k.accent === "amber" ? "text-amber-700" : ""}`}>
                {k.value}
              </div>
              {k.hint ? <div className="text-[11px] text-muted-foreground">{k.hint}</div> : null}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No parks configured.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-card sticky top-0 z-10">
                <tr className="border-b">
                  <th className="px-3 py-2 text-left font-medium">Park</th>
                  <th className="px-2 py-2 text-left font-medium">Supervisor</th>
                  <th className="px-2 py-2 text-left font-medium">Planned area</th>
                  <th className="px-2 py-2 text-left font-medium">Actual area</th>
                  <th className="px-2 py-2 text-center font-medium">Match?</th>
                  <th className="px-2 py-2 text-center font-medium">Before</th>
                  <th className="px-2 py-2 text-center font-medium">Mode</th>
                  <th className="px-2 py-2 text-center font-medium">Photos</th>
                  <th className="px-2 py-2 text-center font-medium">Uniform</th>
                  <th className="px-2 py-2 text-center font-medium">Toolkit</th>
                  <th className="px-2 py-2 text-center font-medium">After</th>
                  <th className="px-2 py-2 text-right font-medium">Size cleaned</th>
                  <th className="px-2 py-2 text-center font-medium">As-per-plan</th>
                  <th className="px-2 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isMismatch = r.plan_match === false;
                  return (
                    <tr
                      key={r.park_id}
                      className={`border-b last:border-0 hover:bg-amber-50/40 ${isMismatch ? "bg-rose-50/40" : ""}`}
                      data-testid={`cleaning-row-${r.park_code}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.park_name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{r.park_code}</div>
                      </td>
                      <td className="px-2 py-2">{r.supervisor?.name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-2 py-2">
                        <div className="text-xs">{r.planned_asset_name || <span className="text-muted-foreground">No plan</span>}</div>
                        {r.planned_asset_type ? (
                          <div className="text-[11px] text-muted-foreground">{ASSET_TYPE_LABEL[r.planned_asset_type] || r.planned_asset_type}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2">
                        <div className="text-xs">{r.actual_asset_name || <span className="text-muted-foreground">—</span>}</div>
                        {r.actual_asset_type ? (
                          <div className="text-[11px] text-muted-foreground">{ASSET_TYPE_LABEL[r.actual_asset_type] || r.actual_asset_type}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-center"><MatchPill value={r.plan_match} /></td>
                      <td className="px-2 py-2 text-center">
                        {r.before_submitted ? (
                          <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">Done</Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-300">Pending</Badge>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center text-xs">
                        {r.before_visit_mode ? (
                          <Badge variant="secondary" className="text-[10px] uppercase">{r.before_visit_mode}</Badge>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center text-xs tabular-nums">
                        {r.before_submitted ? (
                          <span>{r.before_photo_count} / 5</span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center"><StatusPill value={r.uniform_ok} /></td>
                      <td className="px-2 py-2 text-center"><StatusPill value={r.toolkit_ok} /></td>
                      <td className="px-2 py-2 text-center">
                        {r.after_submitted ? (
                          <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">Done</Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-700 border-amber-300">Pending</Badge>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {r.length_km_cleaned != null ? (
                          <span>
                            {r.length_km_cleaned}
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              {(r.actual_asset_type || "").toLowerCase() === "osr" ? "acres" : "km"}
                            </span>
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-2 text-center"><StatusPill value={r.completed_as_per_plan} /></td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDrill(r)}
                          disabled={!r.before_submitted && !r.after_submitted}
                          data-testid={`cleaning-view-${r.park_code}`}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" /> View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Drill-down dialog */}
      <Dialog open={!!drillId} onOpenChange={(v) => { if (!v) { setDrillId(null); setDrill(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-600" />
              {drill?.row?.park_name} <span className="text-xs font-mono text-muted-foreground">{drill?.row?.park_code}</span>
              <span className="text-xs text-muted-foreground">· {fmtDate(date)}</span>
            </DialogTitle>
          </DialogHeader>
          {drill?.loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="cleaning-drill">
              <Section title="Before-cleaning" data={drill?.before} setLightbox={setLightboxSrc} />
              <Section title="After-cleaning" data={drill?.after} setLightbox={setLightboxSrc} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lightboxSrc ? <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} /> : null}
    </AppShell>
  );
}

function Section({ title, data, setLightbox }) {
  if (!data) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-4 text-center text-xs text-muted-foreground">
          <ImageIcon className="mx-auto mb-1 h-5 w-5 opacity-60" />
          {title} — not submitted yet
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3 text-xs">
        <div className="flex items-center justify-between">
          <div className="font-medium">{title}</div>
          <Badge variant="outline">{data.visit_mode}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {/* When the record has multi-asset entries, show them as chips.
              Otherwise fall back to the legacy single asset_name display. */}
          {(data.asset_entries && data.asset_entries.length > 0) ? (
            <div className="col-span-2 flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                Assets ({data.asset_entries.length})
              </span>
              <div className="flex flex-wrap gap-1">
                {data.asset_entries.map((e, idx) => {
                  const t = (e.asset_type || "").toLowerCase();
                  const unit = t === "osr" ? "acres" : "km";
                  return (
                    <span
                      key={`${e.asset_id || idx}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px]"
                    >
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        {ASSET_TYPE_LABEL[e.asset_type] || e.asset_type || "—"}
                      </span>
                      <span className="font-medium">{e.asset_name || "—"}</span>
                      {e.length_km_cleaned != null ? (
                        <span className="text-slate-700">· {e.length_km_cleaned} {unit}</span>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <KV label="Asset" v={data.asset_name || "—"} />
              <KV label="Type" v={ASSET_TYPE_LABEL[data.asset_type] || data.asset_type || "—"} />
            </>
          )}
          {data.kind === "before" ? (
            <>
              <KV label="Uniform" v={<StatusPill value={data.uniform_ok} />} />
              <KV label="Tool kit" v={<StatusPill value={data.toolkit_ok} />} />
              {data.uniform_remark ? <KV label="Uniform remark" v={data.uniform_remark} span={2} /> : null}
              {data.toolkit_remark ? <KV label="Tool kit remark" v={data.toolkit_remark} span={2} /> : null}
              {data.not_visited_reason ? <KV label="Reason" v={data.not_visited_reason} span={2} /> : null}
              <KV label="Plan match" v={<MatchPill value={data.plan_match} />} />
              <KV label="Planned" v={data.planned_asset_name || "—"} />
            </>
          ) : (
            <>
              <KV label="Completion" v={data.completion_mode || "—"} />
              <KV label={(data.asset_type || "").toLowerCase() === "osr" ? "Extent (acres)" : "Length (km)"} v={data.length_km_cleaned != null ? data.length_km_cleaned : "—"} />
              <KV label="As per plan" v={<StatusPill value={data.completed_as_per_plan} />} />
              {data.completed_as_per_plan === false && data.not_completed_as_per_plan_reason ? (
                <KV label="Not-as-per-plan reason" v={data.not_completed_as_per_plan_reason} span={2} />
              ) : null}
              <KV label="Waste removed" v={<StatusPill value={data.waste_removed} />} />
              {data.waste_removed === false && data.waste_not_removed_reason ? (
                <KV label="Waste-not-removed reason" v={data.waste_not_removed_reason} span={2} />
              ) : null}
              {data.remarks ? <KV label="Remarks" v={data.remarks} span={2} /> : null}
            </>
          )}
          <KV label="Submitted" v={fmtDateTime(data.created_at)} span={2} />
          <KV label="By" v={data.supervisor_name || "—"} span={2} />
        </div>
        {Array.isArray(data.photos) && data.photos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {data.photos.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightbox(p.photo_b64?.startsWith("data:") ? p.photo_b64 : `data:image/jpeg;base64,${p.photo_b64}`)}
                className="group relative aspect-square overflow-hidden rounded-md border bg-slate-900/95"
                data-testid={`cleaning-drill-photo-${i}`}
              >
                <img
                  src={p.photo_b64?.startsWith("data:") ? p.photo_b64 : `data:image/jpeg;base64,${p.photo_b64}`}
                  alt=""
                  className="absolute inset-0 h-full w-full object-contain transition-transform group-hover:scale-[1.02]"
                />
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">No photos</div>
        )}
      </CardContent>
    </Card>
  );
}

function KV({ label, v, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xs">{v}</div>
    </div>
  );
}
